import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { ArrowLeft, Mail, Phone, HelpCircle, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import emailjs from '@emailjs/browser';

export default function ContactUs() {
  // Form reference for EmailJS
  const formRef = useRef<HTMLFormElement>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    from_name: '',
    reply_to: '',
    subject: '',
    message: ''
  });
  
  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // EmailJS configuration from environment variables
  const emailjsServiceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const emailjsTemplateId = import.meta.env.VITE_EMAILJS_CONTACT_TEMPLATE_ID;
  const emailjsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

  // Initialize EmailJS
  useEffect(() => {
    if (emailjsPublicKey) {
      emailjs.init(emailjsPublicKey);
    }
  }, [emailjsPublicKey]);
  
  // Handle form submission with EmailJS
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    
    if (!formRef.current) {
      setIsSubmitting(false);
      return;
    }

    // Validate EmailJS configuration
    if (!emailjsServiceId || !emailjsTemplateId || !emailjsPublicKey) {
      setSubmitStatus('error');
      setErrorMessage('Email service is not properly configured. Please try again later or contact us directly.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Create a template params object with all the form data plus recipient
      const templateParams = {
        from_name: formData.from_name,
        reply_to: formData.reply_to,
        subject: formData.subject,
        message: formData.message,
        to_email: 'contact@thefuelcentral.com' // Primary contact email for all inquiries
      };

      // Send the form data directly using EmailJS
      const response = await emailjs.send(
        emailjsServiceId,
        emailjsTemplateId,
        templateParams,
        emailjsPublicKey
      );
      
      if (response.status === 200) {
        // Reset form after successful submission
        setFormData({ from_name: '', reply_to: '', subject: '', message: '' });
        setSubmitStatus('success');
      } else {
        throw new Error(`EmailJS returned status ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      
      // Provide more specific error messages
      if (error?.status === 404) {
        setErrorMessage('Email service account not found. Please contact us directly at contact@thefuelcentral.com');
      } else if (error?.status === 400) {
        setErrorMessage('Invalid email configuration. Please try again or contact us directly.');
      } else if (error?.status === 401) {
        setErrorMessage('Email service authentication failed. Please contact us directly at contact@thefuelcentral.com');
      } else {
        setErrorMessage('There was an error sending your message. Please try again or contact us directly at contact@thefuelcentral.com');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <Link 
        to="/" 
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Home
      </Link>
      
      <h1 className="text-3xl font-bold mb-8">Contact Us</h1>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Get in Touch</h2>
            <p className="text-gray-700 mb-6">
              Have questions or feedback about our gas station valuation tools? We're here to help!
              Our team is ready to assist you with any inquiries.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium">Email Us</p>
                  <a 
                    href="mailto:contact@thefuelcentral.com" 
                    className="text-blue-600 hover:underline"
                  >
                    contact@thefuelcentral.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium">Call Us</p>
                  <a 
                    href="tel:+19512235610" 
                    className="text-blue-600 hover:underline"
                  >
                    +1 (951) 223-5610
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium mb-2">Have Questions?</p>
                <p className="text-gray-600 mb-4">
                  Check our Frequently Asked Questions for quick answers to common questions.
                </p>
                <Link to="/faq">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
                    View FAQ
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Contact Form</h2>
            
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Message Sent!</p>
                  <p className="text-green-700">Thank you for contacting us. We'll respond to your inquiry as soon as possible.</p>
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Something went wrong</p>
                  <p className="text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
            
            <form className="space-y-4" onSubmit={handleSubmit} ref={formRef}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Your name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="reply_to"
                  value={formData.reply_to}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Your email address"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Subject of your message"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="How can we help you?"
                  required
                ></textarea>
              </div>
              
              <Button 
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Submit Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
